import { UserPlus, Mail, Lock, Eye, EyeOff, AlertCircle, User, Phone, MapPin, Map, Search } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, Link } from 'react-router-dom'

const RegisterPage = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [geocoding, setGeocoding] = useState(false)

  const { register, loginWithGoogle } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validation
    if (password !== confirmPassword) {
      setError('As senhas não coincidem')
      return
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres')
      return
    }

    if (!displayName.trim()) {
      setError('Por favor, informe seu nome')
      return
    }

    if (!city.trim()) {
      setError('Por favor, informe sua cidade')
      return
    }

    if (!state.trim()) {
      setError('Por favor, informe seu estado')
      return
    }

    setLoading(true)

    try {
      const options: { phoneNumber?: string; city?: string; state?: string } = {}
      if (phoneNumber.trim()) options.phoneNumber = phoneNumber.trim()
      if (city.trim()) options.city = city.trim()
      if (state.trim()) options.state = state.trim()
      await register(email, password, displayName.trim(), options)
      setEmailSent(true)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Falha no registro. Tente novamente.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setError('')
    setGoogleLoading(true)
    try {
      await loginWithGoogle()
      navigate('/')
    } catch (err: unknown) {
      setError('Erro ao entrar com Google. Tente novamente.')
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleAddressSearch = async () => {
    if (!address.trim()) return
    
    setGeocoding(true)
    setError('')
    
    try {
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
      if (!apiKey) {
        setError('Configuração do Google Maps não disponível. Digite cidade e estado manualmente.')
        setGeocoding(false)
        return
      }
      
      const encodedAddress = encodeURIComponent(address.trim())
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`
      )
      
      const data = await response.json()
      
      // Handle API errors
      if (data.status === 'REQUEST_DENIED') {
        setError('Chave da API do Google Maps inválida ou API não habilitada. Verifique a configuração.')
        return
      }
      
      if (data.status === 'OVER_QUERY_LIMIT') {
        setError('Limite de consultas excedido na API do Google Maps.')
        return
      }
      
      if (data.status === 'OK' && data.results.length > 0) {
        const result = data.results[0]
        
        // Extract city and state from address components
        let foundCity = ''
        let foundState = ''
        
        for (const component of result.address_components) {
          if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
            foundCity = component.long_name
          }
          if (component.types.includes('administrative_area_level_1')) {
            foundState = component.short_name
          }
        }
        
        if (foundCity) setCity(foundCity)
        if (foundState) setState(foundState)
        
        if (!foundCity || !foundState) {
          setError('Endereço encontrado, mas não foi possível identificar cidade e estado. Preencha manualmente.')
        }
      } else {
        setError('Endereço não encontrado. Digite cidade e estado manualmente.')
      }
    } catch (err) {
      setError('Erro ao buscar endereço. Digite cidade e estado manualmente.')
    } finally {
      setGeocoding(false)
    }
  }

  const handleAddressKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddressSearch()
    }
  }

  return (
    <div className="flex items-center justify-center p-4 flex-grow">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-noe-brand animate-pulse" />
            <span className="text-xl font-bold tracking-wide">NOÉ CONNECT</span>
          </div>
          <h1 className="text-4xl font-bold font-serif tracking-tight mt-4">
            Criar nova conta
          </h1>
          <p className="text-noe-400 mt-2">
            Junte-se à comunidade de brasileiros nos EUA
          </p>
        </div>

         {/* Registration card */}
        <div className="bg-noe-900/30 border border-noe-800 rounded-[2rem] p-8 backdrop-blur-xl">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3 text-red-300">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {emailSent ? (
            <div className="text-center py-8">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-noe-brand/10 rounded-full mb-4">
                  <Mail className="w-8 h-8 text-noe-brand" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Verifique seu e-mail</h3>
                <p className="text-noe-400 mb-4">
                  Enviamos um link de verificação para <strong>{email}</strong>
                </p>
                <p className="text-sm text-noe-500 mb-6">
                  Clique no link no e-mail para confirmar sua conta. 
                  Se não encontrar o e-mail, verifique sua pasta de spam.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="py-3 px-6 bg-noe-brand text-white font-medium rounded-xl hover:bg-noe-brandHover transition-colors"
                >
                  Ir para a página inicial
                </button>
                <p className="text-sm text-noe-500 mt-4">
                  Problemas com o e-mail?{' '}
                  <button
                    onClick={() => setEmailSent(false)}
                    className="text-noe-brand hover:text-noe-brandHover font-medium"
                  >
                    Tente novamente
                  </button>
                </p>
              </div>
            </div>
           ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-noe-300 mb-2">
                    Nome completo
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                      placeholder="Seu nome"
                      required
                    />
                  </div>
                </div>

                {/* Campos opcionais */}
                <div>
                  <label className="block text-sm font-medium text-noe-300 mb-2">
                    Número de telefone (opcional)
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-noe-300 mb-2">
                     Buscar endereço (opcional)
                   </label>
                   <div className="relative">
                     <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                     <input
                       type="text"
                       value={address}
                       onChange={(e) => setAddress(e.target.value)}
                       onKeyDown={handleAddressKeyDown}
                       className="w-full pl-12 pr-24 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                       placeholder="Digite um endereço para buscar cidade e estado"
                     />
                     <button
                       type="button"
                       onClick={handleAddressSearch}
                       disabled={geocoding || !address.trim()}
                       className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-noe-brand text-noe-950 text-sm font-medium rounded-lg hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       {geocoding ? 'Buscando...' : 'Buscar'}
                     </button>
                   </div>
                   <p className="text-xs text-noe-500 mt-2">
                     Digite um endereço para preencher automaticamente cidade e estado
                   </p>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-noe-300 mb-2">
                     Cidade *
                   </label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                     <input
                       type="text"
                       value={city}
                       onChange={(e) => setCity(e.target.value)}
                       className="w-full pl-12 pr-4 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                       placeholder="Orlando"
                       required
                     />
                  </div>
                </div>

                <div>
                   <label className="block text-sm font-medium text-noe-300 mb-2">
                     Estado *
                   </label>
                  <div className="relative">
                    <Map className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                     <input
                       type="text"
                       value={state}
                       onChange={(e) => setState(e.target.value)}
                       className="w-full pl-12 pr-4 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                       placeholder="FL"
                       required
                     />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-noe-300 mb-2">
                    E-mail
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                      placeholder="seu@email.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-noe-300 mb-2">
                    Senha
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-noe-500 hover:text-noe-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-noe-500 mt-2">Mínimo de 6 caracteres</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-noe-300 mb-2">
                    Confirmar senha
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-noe-500" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-3 bg-noe-900/50 border border-noe-800 rounded-xl text-noe-100 placeholder-noe-500 focus:outline-none focus:ring-2 focus:ring-noe-brand/50 focus:border-noe-brand transition-all"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-noe-500 hover:text-noe-300 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-6 bg-noe-brand text-white font-medium rounded-xl hover:bg-noe-brandHover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                      Criando conta...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5" />
                      Criar conta
                    </>
                  )}
                </button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-noe-800"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-noe-900/30 text-noe-500">ou continue com</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  className="w-full py-3 px-6 bg-white text-gray-800 font-medium rounded-xl hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-3"
                >
                  {googleLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-800"></div>
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Cadastrar com Google
                    </>
                  )}
                </button>

                <div className="text-center text-noe-500 text-sm">
                  Ao criar uma conta, você concorda com nossos{' '}
                  <a href="#" className="text-noe-brand hover:text-noe-brandHover font-medium">
                    Termos de Serviço
                  </a>{' '}
                  e{' '}
                  <a href="#" className="text-noe-brand hover:text-noe-brandHover font-medium">
                    Política de Privacidade
                  </a>
                </div>
              </form>

              <div className="mt-8 pt-8 border-t border-noe-800">
                <p className="text-center text-noe-500 text-sm">
                  Já tem uma conta?{' '}
                  <Link to="/login" className="text-noe-brand hover:text-noe-brandHover font-medium">
                    Faça login
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Decorative elements */}
        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-6 text-noe-600 text-sm">
            <span>Registro seguro</span>
            <span>•</span>
            <span>Conexão criptografada</span>
            <span>•</span>
            <span>Conformidade GDPR</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage